var _a, _b;
import { DateTime } from "luxon";
// Use namespace import for broader compatibility
import * as rruleLib from 'rrule';
// Attempt to access RRule and Weekday, checking common patterns
const RRule = rruleLib.RRule || ((_a = rruleLib.default) === null || _a === void 0 ? void 0 : _a.RRule);
const Weekday = rruleLib.Weekday || ((_b = rruleLib.default) === null || _b === void 0 ? void 0 : _b.Weekday);
console.log('RRule , ', RRule);
console.log('Weekday , ', Weekday);
// Add a check to see if the import was successful
if (!RRule || !Weekday) {
    console.error("FATAL: Failed to import RRule or Weekday from 'rrule'. Recurring events may not work.");
    // Depending on your application's needs, you might throw an error here
    // or provide fallback values if possible, though it's likely critical.
}
// Use the imported RRule's static Weekday instances (MO, TU, etc.)
// Using 'any' for the type temporarily to avoid TS errors if RRule is initially undefined
export const weekdayMap = {
    0: RRule === null || RRule === void 0 ? void 0 : RRule.MO, // Monday = 0
    1: RRule === null || RRule === void 0 ? void 0 : RRule.TU, // Tuesday = 1
    2: RRule === null || RRule === void 0 ? void 0 : RRule.WE, // Wednesday = 2
    3: RRule === null || RRule === void 0 ? void 0 : RRule.TH, // Thursday = 3
    4: RRule === null || RRule === void 0 ? void 0 : RRule.FR, // Friday = 4
    5: RRule === null || RRule === void 0 ? void 0 : RRule.SA, // Saturday = 5
    6: RRule === null || RRule === void 0 ? void 0 : RRule.SU, // Sunday = 6
};
console.log('weekdayMap , ', weekdayMap);
export const convertByWeekday = (byweekday, opts = {}) => {
    try {
        if (!byweekday || !RRule || !Weekday || typeof weekdayMap !== 'object') {
            if (opts.verbose) {
                console.warn("convertByWeekday prerequisites not met (RRule/Weekday/weekdayMap/byweekday missing)");
            }
            return null;
        }
        const convertedByWeekDay = byweekday === null || byweekday === void 0 ? void 0 : byweekday.map((day) => {
            const daynumber = typeof day === "number" ? day : day === null || day === void 0 ? void 0 : day.weekday;
            if (daynumber === undefined || daynumber === null || daynumber < 0 || daynumber > 6) {
                console.warn(`convertByWeekday: Invalid day number encountered: ${daynumber}`);
                return null;
            }
            const baseWeekday = weekdayMap[daynumber];
            if (!baseWeekday) {
                console.warn(`convertByWeekday: Weekday instance not found in map for day number: ${daynumber}. RRule might not be loaded correctly.`);
                return null;
            }
            const nValue = typeof day === 'object' ? day.n : undefined;
            const formattedDay = nValue ? baseWeekday.nth(nValue) : baseWeekday;
            return formattedDay;
        });
        const result = convertedByWeekDay === null || convertedByWeekDay === void 0 ? void 0 : convertedByWeekDay.filter((d) => d !== null);
        return result && result.length > 0 ? result : null;
    }
    catch (error) {
        console.error("Error in convertByWeekday:", error, "Input:", byweekday);
        return null;
    }
};
export const convertRRule = (rrule, zone, opts = {}) => {
    try {
        if (!RRule) {
            console.error("convertRRule: RRule is not loaded. Cannot process RRule options.");
            return (rrule === null || rrule === void 0 ? void 0 : rrule.origOptions) || rrule || {};
        }
        const rruleoptions = Object.assign({}, ((rrule === null || rrule === void 0 ? void 0 : rrule.origOptions) || rrule || {}));
        const effectiveZone = zone || rruleoptions.tzid || "utc";
        if (opts.verbose) {
            console.log('[convertRRule] Timezone resolution:', {
                inputZone: zone,
                storedTzid: rruleoptions.tzid,
                effectiveZone,
                referenceStartDate: opts.referenceStartDate
            });
        }
        // SIMPLIFIED APPROACH: Use date.start as the source of truth for dtstart
        // If referenceStartDate is provided, ALWAYS use it - this is the event's actual start time
        let dtstart = null;
        let until = null;
        if (opts.referenceStartDate) {
            // date.start is the source of truth - use it directly
            dtstart = DateTime.fromJSDate(opts.referenceStartDate).toUTC();
            if (opts.verbose) {
                console.log('[convertRRule] Using date.start as dtstart:', dtstart.toISO());
            }
        }
        else if (rruleoptions.dtstart) {
            // No reference date provided, use original dtstart
            if (rruleoptions.dtstart instanceof Date) {
                dtstart = DateTime.fromJSDate(rruleoptions.dtstart).toUTC();
            }
            else if (typeof rruleoptions.dtstart === 'string') {
                dtstart = DateTime.fromISO(rruleoptions.dtstart, { zone: effectiveZone }).toUTC();
            }
        }
        // Handle until date normally
        if (rruleoptions.until) {
            if (rruleoptions.until instanceof Date) {
                until = DateTime.fromJSDate(rruleoptions.until).toUTC();
            }
            else if (typeof rruleoptions.until === 'string') {
                until = DateTime.fromISO(rruleoptions.until, { zone: effectiveZone }).toUTC();
            }
        }
        // CRITICAL FIX: If using a specific timezone (tzid), RRule expects dtstart to be "floating"
        // (i.e. a Date object where the UTC components match the LOCAL time in that zone).
        // We must convert the Absolute UTC dtstart to this "Floating/Fake UTC" format.
        if (effectiveZone && effectiveZone !== 'utc' && effectiveZone !== 'local') {
            if (dtstart && dtstart.isValid) {
                // Convert Absolute UTC -> Local Time in effectiveZone
                const inZone = dtstart.setZone(effectiveZone);
                // Create "Fake UTC" with the local components
                dtstart = DateTime.utc(inZone.year, inZone.month, inZone.day, inZone.hour, inZone.minute, inZone.second, inZone.millisecond);
            }
            if (until && until.isValid) {
                // Apply same transformation to until
                const inZone = until.setZone(effectiveZone);
                until = DateTime.utc(inZone.year, inZone.month, inZone.day, inZone.hour, inZone.minute, inZone.second, inZone.millisecond);
            }
        }
        const processedDates = {
            dtstart: (dtstart === null || dtstart === void 0 ? void 0 : dtstart.isValid) ? dtstart.toJSDate() : undefined,
            until: (until === null || until === void 0 ? void 0 : until.isValid) ? until.toJSDate() : undefined,
        };
        if (opts.verbose) {
            console.log('[convertRRule] Raw byweekday before conversion:', rruleoptions.byweekday);
            console.log('[convertRRule] Processed dates:', processedDates);
        }
        const convertedByWeekdayResult = convertByWeekday(rruleoptions.byweekday, opts);
        // Use effective timezone for DST-aware recurrence
        const finalRuleOptions = Object.assign(Object.assign(Object.assign({}, rruleoptions), processedDates), { byweekday: convertedByWeekdayResult, tzid: effectiveZone === 'utc' || effectiveZone === 'local' ? undefined : effectiveZone });
        Object.keys(finalRuleOptions).forEach(key => {
            if (finalRuleOptions[key] === undefined || finalRuleOptions[key] === null) {
                delete finalRuleOptions[key];
            }
        });
        return finalRuleOptions;
    }
    catch (error) {
        console.error("Error in convertRRule:", error, "Input:", rrule);
        return (rrule === null || rrule === void 0 ? void 0 : rrule.origOptions) || rrule || {};
    }
};
//# sourceMappingURL=rrule.js.map